/**
 * BoardCanvas — the react-flow canvas: pan/zoom, draggable cards, freeform
 * sections, and labeled connections. Fully controlled: `nodes`/`edges` are
 * derived from StoryBoardContext + the character-filter highlight set on
 * every render, never duplicated into local react-flow state. Node-drag
 * positions are only written back on drag-stop (never on every drag frame)
 * to keep localStorage writes discrete.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  ReactFlow, ReactFlowProvider, Background, BackgroundVariant, Controls, Panel,
  useReactFlow, ConnectionMode,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button, cx } from '@/components/site/primitives';
import { useStoryBoard } from './context/StoryBoardContext';
import BoardCardNode from './BoardCardNode';
import SectionNode from './SectionNode';
import BoardEdge from './BoardEdge';
import AddNodeMenu from './AddNodeMenu';
import { EDGE_LABEL_SUGGESTIONS } from './constants';
import { computeHighlightSet, computeHighlightedConnectionIds } from './utils/boardHelpers';
import { Layers } from 'lucide-react';

const NODE_TYPE_REGISTRY = { card: BoardCardNode, section: SectionNode };
const EDGE_TYPE_REGISTRY = { boardEdge: BoardEdge };

function EdgeLabelEditor({ connection, onSave, onClose }) {
  const [label, setLabel] = useState(connection.label ?? '');
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[92vw] max-w-sm rounded-2xl border-line bg-card p-5 text-ink">
        <DialogTitle className="font-display text-lg font-semibold text-ink">Label this connection</DialogTitle>
        <Input
          autoFocus
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. reveals, foreshadows, takes place in…"
          className="mt-3 rounded-xl border-line bg-cream"
        />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {EDGE_LABEL_SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setLabel(s)}
              className="rounded-full border border-line px-2.5 py-1 text-[11px] font-medium text-subtle hover:border-coral/40 hover:text-coral"
            >
              {s}
            </button>
          ))}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" icon={false} onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon={false} onClick={() => onSave(label.trim())}>Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BoardCanvasInner({ storyId, board, selectedCharacterIds, onOpenNode, simplified }) {
  const {
    addNode, setNodePosition, deleteNode, addConnection, updateConnection, deleteConnection,
    addSection, updateSection, deleteSection, setNodeSection,
  } = useStoryBoard();
  const { screenToFlowPosition, getIntersectingNodes } = useReactFlow();
  const [editingEdgeId, setEditingEdgeId] = useState(null);

  const highlightSet = useMemo(
    () => computeHighlightSet(board.nodes, board.connections, selectedCharacterIds),
    [board.nodes, board.connections, selectedCharacterIds]
  );
  const highlightedConnIds = useMemo(
    () => computeHighlightedConnectionIds(board.connections, highlightSet),
    [board.connections, highlightSet]
  );
  const muteActive = selectedCharacterIds.length > 0;

  const flowNodes = useMemo(() => {
    const sectionNodes = board.sections.map((s) => ({
      id: s.id,
      type: 'section',
      position: s.position,
      style: { width: s.size?.width ?? 640, height: s.size?.height ?? 420 },
      zIndex: -1,
      data: {
        title: s.title,
        onRename: (title) => updateSection(storyId, s.id, { title }),
        onDelete: () => deleteSection(storyId, s.id),
      },
    }));

    const cardNodes = board.nodes.map((n) => ({
      id: n.id,
      type: 'card',
      position: n.position,
      ...(n.sectionId ? { parentId: n.sectionId, extent: 'parent' } : {}),
      data: {
        ...n,
        cardType: n.type,
        muted: muteActive && !highlightSet.has(n.id),
        onOpen: () => onOpenNode(n.id),
      },
    }));

    return [...sectionNodes, ...cardNodes];
  }, [board.sections, board.nodes, highlightSet, muteActive, storyId, updateSection, deleteSection, onOpenNode]);

  const flowEdges = useMemo(() => board.connections.map((c) => ({
    id: c.id,
    source: c.sourceNodeId,
    target: c.targetNodeId,
    type: 'boardEdge',
    data: {
      label: c.label,
      muted: muteActive && !highlightedConnIds.has(c.id),
      onEditLabel: () => setEditingEdgeId(c.id),
    },
  })), [board.connections, muteActive, highlightedConnIds]);

  const handleNodeDragStop = useCallback((_e, node) => {
    if (node.type === 'section') {
      updateSection(storyId, node.id, { position: node.position });
      return;
    }
    setNodePosition(storyId, node.id, node.position);
    const intersecting = getIntersectingNodes(node).find((n) => n.type === 'section');
    const nextSectionId = intersecting?.id ?? null;
    if (nextSectionId !== (node.data.sectionId ?? null)) {
      setNodeSection(storyId, node.id, nextSectionId);
    }
  }, [storyId, updateSection, setNodePosition, setNodeSection, getIntersectingNodes]);

  const handleConnect = useCallback((connection) => {
    addConnection(storyId, connection.source, connection.target, '');
  }, [storyId, addConnection]);

  const handleNodesDelete = useCallback((nodes) => {
    nodes.forEach((n) => (n.type === 'section' ? deleteSection(storyId, n.id) : deleteNode(storyId, n.id)));
  }, [storyId, deleteNode, deleteSection]);

  const handleEdgesDelete = useCallback((edges) => {
    edges.forEach((e) => deleteConnection(storyId, e.id));
  }, [storyId, deleteConnection]);

  const handleAddNode = useCallback((type) => {
    const center = screenToFlowPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
    // Cascade new cards slightly so they never land perfectly stacked on
    // top of each other (and on top of the last card added).
    const jitter = (board.nodes.length % 6) * 32;
    addNode(storyId, type, { x: center.x - 110 + jitter, y: center.y - 54 + jitter });
  }, [storyId, addNode, screenToFlowPosition, board.nodes.length]);

  const handleAddSection = useCallback(() => {
    const center = screenToFlowPosition({ x: window.innerWidth / 2 - 320, y: window.innerHeight / 2 - 210 });
    addSection(storyId, center);
  }, [storyId, addSection, screenToFlowPosition]);

  const editingEdge = board.connections.find((c) => c.id === editingEdgeId);

  return (
    <div className="sr-board relative h-full w-full">
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        nodeTypes={NODE_TYPE_REGISTRY}
        edgeTypes={EDGE_TYPE_REGISTRY}
        connectionMode={ConnectionMode.Loose}
        onNodeDragStop={handleNodeDragStop}
        onConnect={handleConnect}
        onNodesDelete={handleNodesDelete}
        onEdgesDelete={handleEdgesDelete}
        deleteKeyCode={['Backspace', 'Delete']}
        fitView
        minZoom={0.2}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="var(--color-border)" />
        <Controls
          showInteractive={false}
          className="!rounded-xl !border !border-line !bg-card !shadow-card [&>button]:!border-line [&>button]:!bg-card [&>button]:!text-ink [&>button:hover]:!bg-cream"
        />
        <Panel position="top-left" className="flex items-center gap-2">
          <AddNodeMenu onAdd={handleAddNode} />
          {!simplified && (
            <button
              type="button"
              onClick={handleAddSection}
              className={cx(
                'flex items-center gap-1.5 rounded-full border border-line bg-card px-3.5 py-2 text-xs font-semibold text-ink transition-colors hover:border-coral/40 hover:text-coral'
              )}
            >
              <Layers className="h-3.5 w-3.5" /> Section
            </button>
          )}
        </Panel>
      </ReactFlow>

      {editingEdge && (
        <EdgeLabelEditor
          connection={editingEdge}
          onClose={() => setEditingEdgeId(null)}
          onSave={(label) => { updateConnection(storyId, editingEdge.id, { label }); setEditingEdgeId(null); }}
        />
      )}
    </div>
  );
}

export default function BoardCanvas(props) {
  return (
    <ReactFlowProvider>
      <BoardCanvasInner {...props} />
    </ReactFlowProvider>
  );
}
